import { NgIf } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { RouterOutlet } from '@angular/router';
import { AbilityUpdaterService } from '@core/services/ability/ability-updater.service';
import { FirebaseWorkerService } from '@core/services/firebase/firebase-worker.service';
import { NavigationHistoryService } from '@core/services/navigation-history.service';
import { Logout, RefreshToken } from '@core/store/auth/auth.actions';
import { AuthState } from '@core/store/auth/auth.state';
import { SetCurrentTransactionId } from '@core/store/job-updates/job-updates.actions';
import { LoadOrganizationEntitlementsCapabilityData } from '@core/store/shared/shared.actions';
import { SharedState } from '@core/store/shared/shared.state';
import { Store } from '@ngxs/store';
import { Subject } from 'rxjs';
import { filter, map, mergeMap, take, takeUntil, tap } from 'rxjs/operators';
import { GoogleMapsLoaderService } from './shared/services/google-maps-loader.service';

@Component({
  standalone: true,
  imports: [RouterOutlet, NgIf, MatProgressSpinnerModule],
  selector: 'hmt-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'WebApp';

  private readonly isSpinnerEnabled = this.store.select(SharedState.getSpinnerStatus);
  private readonly notifications$ = this.firebaseWorkerService.notifications$;
  private readonly isAuthenticated = this.store.select(AuthState.isAuthenticated);
  private readonly user = this.store.select(AuthState.getUser);

  private unsubscribe: Subject<void> = new Subject();

  spinnerPros: { spinner: boolean; spinnerMessage: string } = { spinner: false, spinnerMessage: '' };

  constructor(
    private readonly store: Store,
    private readonly googleMapsLoader: GoogleMapsLoaderService,
    private readonly firebaseWorkerService: FirebaseWorkerService,
    private readonly navigationHistory: NavigationHistoryService, // Initialize early to track navigation from app start
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    private readonly _abilityUpdater: AbilityUpdaterService // eagerly initialize ability tracking
  ) {}

  ngOnInit(): void {
    this.store.dispatch(new SetCurrentTransactionId());

    // Only bootstrap authenticated app data when the user is genuinely logged
    // in AND not sitting on a public /auth page (login, invite activation, MFA
    // verify, etc.). A stale persisted token makes `isAuthenticated` truthy, so
    // firing these authenticated calls on /auth/* drew 401s that cascaded into
    // refresh -> logout and bounced invitees off the activation screen.
    const isAuthenticated = this.store.selectSnapshot(AuthState.isAuthenticated);
    const isPublicAuthRoute = window.location.pathname.startsWith('/auth');
    const canBootstrapAppData = isAuthenticated && !isPublicAuthRoute;

    if (canBootstrapAppData) {
      this.user
        .pipe(
          take(1),
          mergeMap(user => {
            return this.store.dispatch(new LoadOrganizationEntitlementsCapabilityData(user.orgId));
          })
        )
        .subscribe();
    }

    this.isSpinnerEnabled
      .pipe(
        tap(result => (this.spinnerPros = result)),
        takeUntil(this.unsubscribe)
      )
      .subscribe();

    // Dispatch refresh token action on every page refresh (authenticated app pages only)
    if (canBootstrapAppData) {
      this.store.dispatch(new RefreshToken());
    }

    // Load Google Maps script
    this.googleMapsLoader
      .loadGoogleMaps()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        error: err => console.error('Failed to load Google Maps:', err),
      });

    this.listenToLoggedInUserAccessChanges();
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  loadScript(name: string) {
    return new Promise((resolve, reject) => {
      try {
        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.src = name;
        document.getElementsByTagName('head')[0].appendChild(script);
        console.log('Script Loaded');
        resolve('Ok');
      } catch (e) {
        reject(`Error ${e}`);
      }
    });
  }

  listenToLoggedInUserAccessChanges() {
    this.notifications$
      .pipe(
        takeUntil(this.unsubscribe),
        filter(notification => {
          const event = notification.event;
          return event === 'USER_ACCESS_REVOKED' || event === 'USER_ACCESS_CHANGED';
        }),
        map(notification => {
          return {
            title: notification.titleTemplate,
            body: notification.bodyTemplate,
          };
        })
      )
      .subscribe(() => {
        this.store.dispatch(new Logout());
      });
  }
}
